import { useState, useCallback, useRef } from 'react';
import React from 'react';
import ConfirmDialog from './index';

// ─── useConfirm hook ──────────────────────────────────────
// Promise-based replacement for window.confirm() using the styled
// <ConfirmDialog>. Callsites just do:
//
//   const { confirm, ConfirmPortal } = useConfirm();
//   ...
//   const handleDelete = async () => {
//     const ok = await confirm({
//       title: 'Delete broker?',
//       message: 'This cannot be undone. An audit entry is recorded.',
//       confirmText: 'Delete',
//       variant: 'danger',
//     });
//     if (!ok) return;
//     await api.delete(...);
//   };
//   ...
//   return (<>{...jsx...}{ConfirmPortal}</>);
//
// The hook owns the modal state internally, so callers don't need to
// manage pendingState / onCancel plumbing. Resolves the promise with
// true on confirm, false on cancel/escape/backdrop.
//
// Note: `ConfirmPortal` must be rendered inside your component tree
// for the dialog to actually mount — it's a JSX node, not a portal-
// bound side effect. That's what makes the modal a controlled part
// of your component lifecycle (unmounts when the parent unmounts).
export default function useConfirm() {
  const [state, setState] = useState(null); // null | { options }
  const resolveRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ options });
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setState(null);
    resolve && resolve(true);
  }, []);

  const handleCancel = useCallback(() => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setState(null);
    resolve && resolve(false);
  }, []);

  const ConfirmPortal = state
    ? <ConfirmDialog {...state.options} onConfirm={handleConfirm} onCancel={handleCancel} />
    : null;

  return { confirm, ConfirmPortal };
}
